import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCurrencyUsd } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { useLocale } from '../../contexts/LocaleContext'
import { formatAICost, formatTokens } from '../../lib/aiUsage'
import { getJobTypeName } from '../../api/jobs'
import { Skeleton } from '../ui/skeleton'
import { cn } from '../../lib/utils'

interface AIUsageBucket {
    cost_usd?: number
    input_tokens?: number
    output_tokens?: number
    executions?: number
}

interface AIUsageRow extends AIUsageBucket {
    type?: string
    month?: string
}

interface AIUsageReport {
    from?: string
    to?: string
    totals?: AIUsageBucket
    by_job_type?: AIUsageRow[]
    by_month?: AIUsageRow[]
    budget?: number
    spent?: number
    remaining?: number | null
    agent_subscription?: {
        input_tokens?: number
        output_tokens?: number
        list_cost_usd?: number
        runs?: number
    }
}

/** An agent mission in the by-type breakdown (spec 2026-10-21-17). */
const AGENT_RUN_PREFIX = 'agent_run:'

/** How many job types the card names before folding the rest into "other". */
const TOP_JOB_TYPES = 4

/**
 * The org-wide AI spend card on the jobs page (spec 2026-08-16-08 §4).
 *
 * Deliberately not a dashboard: the three questions worth answering at a glance
 * are "what did we spend over the window", "which job types burn it", and
 * "where does that leave the monthly budget". Anything deeper belongs on a page
 * of its own, which this spec explicitly defers.
 *
 * The card disappears entirely when the organization has never called a paid
 * model — an all-zero card is noise on the job list of a stack with no Gemini
 * key configured.
 */
export function JobsAIUsageCard({ org }: { org: string }) {
    const { t } = useTranslation('jobs')
    const { locale } = useLocale()

    const { data, isLoading, error } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/jobs/ai-usage',
        { params: { path: { orgCode: org } } },
    )

    const report = data as AIUsageReport | undefined
    const totals = report?.totals
    const subscription = report?.agent_subscription
    const subscriptionTokens = (subscription?.input_tokens ?? 0) + (subscription?.output_tokens ?? 0)

    // The breakdown is already sorted by descending cost server-side; the tail
    // is summed rather than dropped so the rows still add up to the total.
    const { top, otherCost } = useMemo(() => {
        const rows = report?.by_job_type ?? []
        return {
            top: rows.slice(0, TOP_JOB_TYPES),
            otherCost: rows.slice(TOP_JOB_TYPES).reduce((sum, row) => sum + (row.cost_usd ?? 0), 0),
        }
    }, [report])

    if (isLoading) {
        return <Skeleton className="mb-6 h-24 w-full" data-testid="jobs-ai-usage-loading" />
    }

    // A failed fetch must not take the job list down with it: the list is what
    // the page is for, this card is context.
    if (error || !totals || (!totals.cost_usd && subscriptionTokens === 0)) {
        return null
    }

    const budget = report?.budget ?? 0
    const spent = report?.spent ?? 0
    const remaining = report?.remaining
    const budgetRatio = budget > 0 ? Math.min(spent / budget, 1) : 0
    const overBudget = budget > 0 && spent >= budget

    return (
        <section
            data-testid="jobs-ai-usage"
            className="mb-6 rounded-lg border border-border bg-card p-4"
        >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <AppIcon path={mdiCurrencyUsd} size="md" />
                    {t('ai_usage.org_title')}
                </h2>
                <span className="text-2xl font-bold text-foreground" data-testid="jobs-ai-usage-cost">
                    {formatAICost(totals.cost_usd ?? 0, locale)}
                </span>
                <span className="text-sm text-muted-foreground" data-testid="jobs-ai-usage-tokens">
                    {t('ai_usage.tokens', {
                        input: formatTokens(totals.input_tokens ?? 0, locale),
                        output: formatTokens(totals.output_tokens ?? 0, locale),
                    })}
                </span>
                <span className="text-xs text-muted-foreground">
                    {t('ai_usage.executions', { count: totals.executions ?? 0 })}
                </span>
            </div>

            {top.length > 0 && (
                <ul
                    className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
                    data-testid="jobs-ai-usage-by-type"
                >
                    {top.map(row => (
                        <li key={row.type}>
                            <span className="text-foreground">
                                {row.type === 'assistant_router'
                                    ? t('ai_usage.assistant_router')
                                    : row.type?.startsWith(AGENT_RUN_PREFIX)
                                    ? t('ai_usage.agent_run_type', { mission: row.type.slice(AGENT_RUN_PREFIX.length) })
                                    : t(`types.${row.type}`, { defaultValue: getJobTypeName(row.type ?? '') })}
                            </span>{' '}
                            {formatAICost(row.cost_usd ?? 0, locale)}
                        </li>
                    ))}
                    {otherCost > 0 && (
                        <li data-testid="jobs-ai-usage-other">
                            <span className="text-foreground">{t('ai_usage.other_types')}</span>{' '}
                            {formatAICost(otherCost, locale)}
                        </li>
                    )}
                </ul>
            )}

            {subscriptionTokens > 0 && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="jobs-ai-usage-agent-subscription">
                    {t('ai_usage.agent_subscription', {
                        input: formatTokens(subscription?.input_tokens ?? 0, locale),
                        output: formatTokens(subscription?.output_tokens ?? 0, locale),
                        cost: formatAICost(subscription?.list_cost_usd ?? 0, locale),
                        count: subscription?.runs ?? 0,
                    })}
                </p>
            )}

            {/*
              The budget is reported, never enforced (spec §5) — an exhausted
              bar changes nothing about what the platform will do next, so it
              warns rather than alarms.
            */}
            <div className="mt-3 border-t border-border pt-3 text-xs" data-testid="jobs-ai-usage-budget">
                {budget > 0 ? (
                    <>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="text-muted-foreground">
                                {t('ai_usage.budget_month', {
                                    spent: formatAICost(spent, locale),
                                    budget: formatAICost(budget, locale),
                                })}
                            </span>
                            <span
                                className={cn('font-medium', overBudget ? 'text-warning' : 'text-foreground')}
                                data-testid="jobs-ai-usage-remaining"
                            >
                                {t('ai_usage.remaining', {
                                    amount: formatAICost(remaining ?? 0, locale),
                                })}
                            </span>
                        </div>
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                                className={cn('h-full rounded-full', overBudget ? 'bg-warning' : 'bg-primary')}
                                style={{ width: `${budgetRatio * 100}%` }}
                            />
                        </div>
                        <p className="mt-2 text-muted-foreground">{t('ai_usage.not_enforced')}</p>
                    </>
                ) : (
                    <span className="text-muted-foreground" data-testid="jobs-ai-usage-unlimited">
                        {t('ai_usage.budget_unlimited', { spent: formatAICost(spent, locale) })}
                    </span>
                )}
            </div>
        </section>
    )
}
